import type { LevelId, RoundResult } from '../types';

export class ScoreSystem {
  private points = 0;
  private bytes = 0;
  private powerUps = 0;
  private hits = 0;
  private wrongRoutes = 0;
  private currentCombo = 0;
  private bestCombo = 0;
  private signalStreak = 0;
  private awarded = 0;

  collectByte(basePoints = 10): number {
    this.bytes += 1;
    this.signalStreak += 1;
    const multiplier = Math.min(4, 1 + Math.floor((this.signalStreak - 1) / 5));
    this.awarded = basePoints * multiplier;
    this.points += this.awarded;
    return this.points;
  }

  resetCombo(): void {
    this.currentCombo = 0;
    this.signalStreak = 0;
  }

  connectComponent(multiplier = 1): number {
    this.currentCombo += 1;
    this.bestCombo = Math.max(this.bestCombo, this.currentCombo);
    this.awarded = 125 * Math.max(1, multiplier);
    this.points += this.awarded;
    return this.points;
  }

  awardBonus(points: number): number {
    this.awarded = Math.max(0, Math.round(points));
    this.points += this.awarded;
    return this.points;
  }

  usePowerUp(): number {
    this.powerUps += 1;
    this.points += 50;
    return this.points;
  }

  registerHit(): number {
    this.hits += 1;
    this.resetCombo();
    this.points = Math.max(0, this.points - 100);
    return this.points;
  }

  registerWrongRoute(): number {
    this.wrongRoutes += 1;
    return this.points;
  }

  finish(
    levelId: LevelId,
    completed: boolean,
    elapsedSeconds: number,
    remainingSeconds: number,
    correctConnections: number,
    totalConnections: number,
    language: 'community' | 'packet' = 'packet',
  ): RoundResult {
    const bonuses: { label: string; points: number }[] = [];
    if (completed) bonuses.push({ label: 'Connection Complete', points: 500 });
    if (completed && this.wrongRoutes === 0) bonuses.push({ label: 'Perfect Route', points: 300 });
    if (completed && this.hits === 0) bonuses.push({ label: language === 'community' ? 'Clean Connection Bonus' : 'No Malware Hits', points: 250 });
    if (completed) bonuses.push({ label: 'Speed Bonus', points: Math.max(0, Math.floor(remainingSeconds) * 10) });
    const bonusPoints = bonuses.reduce((total, bonus) => total + bonus.points, 0);

    return {
      levelId,
      score: this.points + bonusPoints,
      elapsedSeconds: Math.max(0, Math.round(elapsedSeconds)),
      remainingSeconds: Math.max(0, Math.round(remainingSeconds)),
      correctConnections,
      totalConnections,
      bytes: this.bytes,
      powerUps: this.powerUps,
      malwareHits: this.hits,
      wrongRoutes: this.wrongRoutes,
      highestCombo: this.bestCombo,
      routeAccuracy: totalConnections === 0
        ? 100
        : Math.max(0, Math.round((correctConnections / (totalConnections + this.wrongRoutes)) * 100)),
      completed,
      bonuses,
    };
  }

  get score(): number {
    return this.points;
  }

  get combo(): number {
    return this.currentCombo;
  }

  get highestCombo(): number {
    return this.bestCombo;
  }

  get lastAwarded(): number {
    return this.awarded;
  }
}
